import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentClientIc } from '../../../../components/icons';
import { CampaignsTableProps } from '../types';
import { getTableColumns } from '../constants';
import CampaignCell from './CampaignCell';
import StatisticsCell from './StatisticsCell';
import StatusCell from './StatusCell';
import AddedCell from './AddedCell';
import IdCell from './IdCell';

const CampaignsTable: React.FC<CampaignsTableProps> = ({
  campaigns,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslations();
  const columns = getTableColumns(t);

  const renderCell = useCallback((campaign: any, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <IdCell campaign={campaign} />;
      case 'campaign':
        return <CampaignCell campaign={campaign} />;
      case 'statistics':
        return <StatisticsCell campaign={campaign} />;
      case 'status':
        return <StatusCell campaign={campaign} />;
      case 'added':
        return <AddedCell campaign={campaign} />;
      default:
        return null;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={campaigns}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('campaigns.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('campaigns.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CampaignsTable;
